import React, { useState } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Breadcrumbs, CircularProgress, Stack, Typography, Box } from "@mui/material";
import { CaretRight } from "@phosphor-icons/react";
import Grid from "@mui/material/Unstable_Grid2";

import type { GoalMenuState } from "components/ClientDetails/Sections/GoalsAndTargets/shared/types";
import GoalDetailTitleBar from "components/ClientDetails/Sections/GoalsAndTargets/Goals/GoalDetailTitleBar";
import GoalActionMenu from "./Goals/GoalActionMenu";
import MasteryBanner from "./MasteryBanner";
import GoalTargetsList from "./Targets/GoalTargetsList";
import TargetActionMenu from "./Targets/TargetActionMenu";
import GoalOverviewCard from "./Goals/GoalOverviewCard";
import GoalNotesCard from "./Goals/GoalNotesCard";
import ResourcesCard from "./shared/ResourcesCard";
import { useTheme } from "@mui/material/styles";
import getStatusStyles from "./shared/StatusPill/getStatusColor";
import { ChartEmptyState, ChartErrorState } from "./shared/ChartStateCard";
import GoalCumulativeRecordChart from "./Goals/GoalCumulativeRecordChart";
import GoalBuilderPanel from "./Goals/GoalBuilderPanel";
import DiscontinueGoalPanel from "./Goals/DiscontinueGoalPanel";
import PutOnHoldPanel from "./Goals/PutOnHoldPanel";
import ArchiveGoalPanel from "./Goals/ArchiveGoalPanel";
import DeleteGoalPanel from "./Goals/DeleteGoalPanel";
import type { TargetActionState } from "./Goals/GoalDispositionPanel";
import {
  mapGoalResources,
  mapGoalType,
  mapMasteryCriteriaNotes,
  mapInstructionNotes,
  mapBehaviorReductionNotes,
  allTargetsMet,
} from "./utils/goalMappers";
import {
  selectActiveTargetOptions,
  selectGoalsForMove,
  selectMoveDestinationGoals,
  selectTargetListRows,
  buildTargetDispositions,
} from "./utils/goalSelectors";
// API hooks
import useGetDetailGoal from "hooks/api/goals/useGetDetailGoal";
import useGetGoalMasteryHistory from "hooks/api/goals/useGetGoalMasteryHistory";
import { useTargetFilters } from "./Targets/GoalTargetsList/useTargetFilters";
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";
import useArchiveGoal from "hooks/api/goals/useArchiveGoal";
import useUnarchiveGoal from "hooks/api/goals/useUnarchiveGoal";
import useDeleteGoal from "hooks/api/goals/useDeleteGoal";
import useDuplicateTarget from "hooks/api/targets/useDuplicateTarget";
import useArchiveTarget from "hooks/api/targets/useArchiveTarget";
import useUnarchiveTarget from "hooks/api/targets/useUnarchiveTarget";
import useDeleteTarget from "hooks/api/targets/useDeleteTarget";
import useMoveTarget from "hooks/api/targets/useMoveTarget";

import MoveTargetPanel from "./Targets/MoveTargetPanel";
import DeleteTargetDialog from "./Targets/DeleteTargetDialog";

type ActivePanel = "edit" | "discontinue" | "putOnHold" | "archive" | "delete" | null;

/**
 * Goal detail view — rendered at /clients/:id/goals-targets/:goalId.
 *
 * Displays the full detail of a single goal including targets, trend chart,
 * mastery criteria, and clinical metadata.
 */
const GoalDetail: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();

  const { id: clientId, slug: goalIdStr } = useParams<{ id: string; slug?: string }>();
  const [goalMenu, setGoalMenu] = useState<GoalMenuState>({ anchorEl: null, goalId: null });
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [targetMenu, setTargetMenu] = useState<{
    anchorEl: HTMLElement | null;
    targetId: string | null;
  }>({ anchorEl: null, targetId: null });
  /**
   * Dialog state for target operations triggered from the row action menu.
   * `targetId` and `targetName` are captured at open-time so the dialogs work
   * even after the menu closes and `targetMenu.targetId` resets.
   */
  const [moveTargetState, setMoveTargetState] = useState<{
    open: boolean;
    targetId: string | null;
    targetName: string;
  }>({ open: false, targetId: null, targetName: "" });
  const [deleteTargetState, setDeleteTargetState] = useState<{
    open: boolean;
    targetId: string | null;
    targetName: string;
  }>({ open: false, targetId: null, targetName: "" });

  // Dismiss for the mastery banner.
  const [isMasteryBannerDismissed, setIsMasteryBannerDismissed] = useState(false);

  const goalId = Number(goalIdStr);

  const { goal, isGoalLoading } = useGetDetailGoal(goalId);

  // Goal-level cumulative record — weekly count of targets that reached mastery.
  const {
    series: masteryHistorySeries,
    isMasteryHistoryLoading,
    errorMasteryHistory,
    refetch: refetchMasteryHistory,
  } = useGetGoalMasteryHistory(goalId);

  const { archiveGoalAction, isArchivingGoal } = useArchiveGoal();
  const { unarchiveGoalAction } = useUnarchiveGoal();
  const { deleteGoalAction, isDeletingGoal } = useDeleteGoal();

  // --- Target mutations (row menu actions) -------------------------------
  const { duplicateTargetAction } = useDuplicateTarget();
  const { archiveTargetAction } = useArchiveTarget();
  const { unarchiveTargetAction } = useUnarchiveTarget();
  const { deleteTargetAction, isDeletingTarget } = useDeleteTarget({
    onSuccess: () => setDeleteTargetState({ open: false, targetId: null, targetName: "" }),
  });
  const { moveTargetAction, isMovingTarget } = useMoveTarget({
    onSuccess: () => setMoveTargetState({ open: false, targetId: null, targetName: "" }),
  });

  const { goals: allGoals } = useGetListOfGoals(clientId, { includeArchived: false });
  const moveDestinationGoals = selectMoveDestinationGoals(allGoals, goal);
  const targets = selectTargetListRows(goal?.targets);

  const {
    filteredTargets,
    totalCount: totalTargetCount,
    activeFilters: targetActiveFilters,
    filterOptions: targetFilterOptions,
    needsAttentionCount,
    isNeedsAttentionActive,
    onAddFilter: handleAddTargetFilter,
    onDeleteFilter: handleDeleteTargetFilter,
    onClearFilters: handleClearTargetFilters,
    onNeedsAttentionClick: handleNeedsAttentionClick,
  } = useTargetFilters(targets);

  const handleMenuOpen = (goalId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    setGoalMenu({ anchorEl: e.currentTarget, goalId });
  };

  const handleMenuClose = () => setGoalMenu({ anchorEl: null, goalId: null });

  const openPanel = (panel: ActivePanel) => {
    setActivePanel(panel);
    handleMenuClose();
  };

  const closePanel = () => setActivePanel(null);

  const navigateBackToList = () => {
    history.push(`/clients/${clientId}/goals-targets`);
  };

  /** Look up the captured target name from the current row menu, falling back to a generic label. */
  const getMenuTargetName = (tid: string | null) =>
    targets.find((t) => t.id === tid)?.name ?? "this target";

  // ---------------------------------------------------------------------------
  // Menu handlers
  // ---------------------------------------------------------------------------
  const handleTargetNavigate = (targetId: string) => {
    history.push(`/clients/${clientId}/goals-targets/${goalId}/target/${targetId}`);
  };

  const handleAddTarget = () => {
    history.push(`/clients/${clientId}/goals-targets/${goalId}/target/new`);
  };

  const handleTargetMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, targetId: string) => {
    setTargetMenu({ anchorEl: event.currentTarget, targetId });
  };

  const handleTargetMenuClose = () => setTargetMenu({ anchorEl: null, targetId: null });

  const handleEditTarget = () => {
    if (!targetMenu.targetId) return;
    const tid = targetMenu.targetId;
    handleTargetMenuClose();
    history.push(`/clients/${clientId}/goals-targets/${goalId}/target/${tid}?mode=edit`);
  };

  const handleDuplicateTarget = () => {
    if (!targetMenu.targetId) return;
    duplicateTargetAction({ targetId: Number(targetMenu.targetId), goalId });
  };

  const handleArchiveTarget = () => {
    if (!targetMenu.targetId) return;
    archiveTargetAction({ targetId: Number(targetMenu.targetId), goalId });
  };

  const handleUnarchiveTarget = () => {
    if (!targetMenu.targetId) return;
    unarchiveTargetAction({ targetId: Number(targetMenu.targetId), goalId });
  };

  const handleOpenMoveTarget = () => {
    if (!targetMenu.targetId) return;
    setMoveTargetState({
      open: true,
      targetId: targetMenu.targetId,
      targetName: getMenuTargetName(targetMenu.targetId),
    });
  };

  const handleOpenDeleteTarget = () => {
    if (!targetMenu.targetId) return;
    setDeleteTargetState({
      open: true,
      targetId: targetMenu.targetId,
      targetName: getMenuTargetName(targetMenu.targetId),
    });
  };

  const handleConfirmDeleteTarget = () => {
    if (!deleteTargetState.targetId) return;
    deleteTargetAction({ targetId: Number(deleteTargetState.targetId), goalId });
  };

  const handleConfirmMoveTarget = (destinationGoalId: string) => {
    if (!moveTargetState.targetId) return;
    moveTargetAction({
      targetId: Number(moveTargetState.targetId),
      sourceGoalId: goalId,
      destinationGoalId: Number(destinationGoalId),
    });
  };

  if (isGoalLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress aria-label="Loading goal" />
      </Box>
    );
  }

  if (!goal) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const activeTargets = selectActiveTargetOptions(goal.targets);
  const availableGoalsForMove = selectGoalsForMove(allGoals, goal.id);

  const resources = mapGoalResources(goal.resources);
  const goalType = mapGoalType(goal.goal_type);
  const masteryNotes = mapMasteryCriteriaNotes(goal);
  const instructionNotes = mapInstructionNotes(goal);
  const behaviorReductionNotes = mapBehaviorReductionNotes(goal);
  // Mastery evaluation considers only non-archived targets, even though the
  // list itself displays archived ones.
  const unarchivedTargets = targets.filter((t) => !t.isArchived);
  const showMasteryBanner = allTargetsMet(unarchivedTargets) && !isMasteryBannerDismissed;
  const metCount = unarchivedTargets.filter((t) => t.status === "met").length;

  // Cumulative record chart inputs, derived from the mastery-history series.
  // Phase 1: the step line only — the enriched mastery-event dots and phase
  // markers need data the endpoint doesn't return yet (see backend follow-up).
  const masteryLabels = masteryHistorySeries.map((point) => `W${point.week}`);
  const masteryData = masteryHistorySeries.map((point) => point.targets_mastered);
  // Y-axis max: never clip a cumulative count that outruns the current
  // unarchived total (e.g. a mastered target later archived).
  const cumulativeMax = masteryData.length ? masteryData[masteryData.length - 1] : 0;
  const totalTargetsForChart = Math.max(unarchivedTargets.length, cumulativeMax, 1);
  const masteredSummary = `${metCount} of ${unarchivedTargets.length} targets mastered`;
  const masteryChartColor = getStatusStyles(theme)[goal.status].dotColor;

  return (
    <Stack direction="column" gap={5} p={6}>
      {/* Back navigation */}
      <Breadcrumbs separator={<CaretRight />} aria-label="breadcrumb">
        <Link to={`/clients/${clientId}/goals-targets`} style={{ textDecoration: "none" }}>
          <Typography variant="body2">Goals</Typography>
        </Link>

        <Typography variant="body2" aria-current="page">
          {goal.title}
        </Typography>
      </Breadcrumbs>

      {/* Title bar */}
      <GoalDetailTitleBar
        goalName={goal.title}
        description={goal.description ?? ""}
        domain={goal.domain}
        status={goal.status}
        onAddToProgram={() => {}}
        onMenuOpen={handleMenuOpen(goal.id)}
      />

      <Grid container columnSpacing={5} rowSpacing={5} columns={12}>
        <Grid sm={12} md={8}>
          {showMasteryBanner && (
            <MasteryBanner
              level="allTargetsMet"
              title="All targets met — ready for mastery review."
              detail={`${metCount} of ${unarchivedTargets.length} targets met`}
              onDismiss={() => setIsMasteryBannerDismissed(true)}
            />
          )}

          {/* Goal-level cumulative record — running count of mastered targets.
              Phase 1 renders the step line from GET /goals/:id/mastery-history;
              mastery-event dots + phase markers await a richer endpoint. */}
          <Box>
            {isMasteryHistoryLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
                <CircularProgress aria-label="Loading mastery history" />
              </Box>
            ) : errorMasteryHistory ? (
              <ChartErrorState minHeight={280} onRetry={() => refetchMasteryHistory()} />
            ) : unarchivedTargets.length === 0 ? (
              <ChartEmptyState
                minHeight={280}
                title="Nothing to chart."
                description="Add targets to render mastery chart."
              />
            ) : masteryData.length === 0 ? (
              <ChartEmptyState
                minHeight={280}
                description="Mastery history will chart here once this goal's targets start reaching mastery."
              />
            ) : (
              <GoalCumulativeRecordChart
                labels={masteryLabels}
                data={masteryData}
                totalTargets={totalTargetsForChart}
                masteredSummary={masteredSummary}
                color={masteryChartColor}
              />
            )}
          </Box>

          <Box mt={5}>
            <GoalTargetsList
              targets={filteredTargets}
              totalTargetCount={totalTargetCount}
              filterOptions={targetFilterOptions}
              activeFilters={targetActiveFilters}
              needsAttentionCount={needsAttentionCount}
              isNeedsAttentionActive={isNeedsAttentionActive}
              onAddFilter={handleAddTargetFilter}
              onDeleteFilter={handleDeleteTargetFilter}
              onClearFilters={handleClearTargetFilters}
              onNeedsAttentionClick={handleNeedsAttentionClick}
              onTargetNavigate={handleTargetNavigate}
              onTargetMenuOpen={handleTargetMenuOpen}
              onAddTarget={handleAddTarget}
            />
          </Box>
        </Grid>

        <Grid sm={12} md={4}>
          <GoalOverviewCard
            title="Goal Overview"
            domain={goal.domain}
            status={goal.status}
            goalType={goalType}
            behaviorMeasured={goal.behavior_subject}
          />

          {behaviorReductionNotes.length > 0 && (
            <Box mt={5}>
              <GoalNotesCard title="Behavior reduction" criteriaNotes={behaviorReductionNotes} />
            </Box>
          )}

          {masteryNotes.length > 0 && (
            <Box mt={5}>
              <GoalNotesCard title="Mastery criteria" criteriaNotes={masteryNotes} />
            </Box>
          )}

          {instructionNotes.length > 0 && (
            <Box mt={5}>
              <GoalNotesCard title="Instructions" criteriaNotes={instructionNotes} />
            </Box>
          )}

          {resources.length > 0 && (
            <Box mt={5}>
              <ResourcesCard resources={resources} />
            </Box>
          )}
        </Grid>
      </Grid>

      <GoalActionMenu
        anchorEl={goalMenu.anchorEl}
        open={Boolean(goalMenu.anchorEl)}
        onClose={handleMenuClose}
        onEdit={() => openPanel("edit")}
        onPutOnHold={() => openPanel("putOnHold")}
        onDiscontinue={() => openPanel("discontinue")}
        onArchive={() => openPanel("archive")}
        onUnarchive={() => {
          unarchiveGoalAction({ goalId, clientId });
          handleMenuClose();
        }}
        onDelete={() => openPanel("delete")}
        isArchived={Boolean(goal.is_archived)}
      />

      {/* Goal actions menu */}
      <GoalBuilderPanel
        open={activePanel === "edit"}
        onClose={closePanel}
        mode="edit"
        clientId={clientId}
        goal={goal}
      />

      <DiscontinueGoalPanel
        open={activePanel === "discontinue"}
        onClose={closePanel}
        goalName={goal.title}
        goalId={goal.id}
        clientId={clientId}
      />

      <PutOnHoldPanel
        open={activePanel === "putOnHold"}
        onClose={closePanel}
        goalName={goal.title}
        goalId={goal.id}
        clientId={clientId}
      />

      <ArchiveGoalPanel
        open={activePanel === "archive"}
        onClose={closePanel}
        goalName={goal.title}
        targets={activeTargets}
        availableGoals={availableGoalsForMove}
        isLoading={isArchivingGoal}
        onArchive={(actions: TargetActionState[], rationale: string) => {
          archiveGoalAction(
            {
              goalId: goal.id,
              clientId,
              targetDispositions: buildTargetDispositions(actions, "archive"),
              rationale,
            },
            {
              onSuccess: () => {
                closePanel();
                navigateBackToList();
              },
            }
          );
        }}
      />

      <DeleteGoalPanel
        open={activePanel === "delete"}
        onClose={closePanel}
        goalName={goal.title}
        targets={activeTargets}
        availableGoals={availableGoalsForMove}
        isLoading={isDeletingGoal}
        onDelete={(actions: TargetActionState[], rationale: string) => {
          deleteGoalAction(
            {
              goalId: goal.id,
              clientId,
              targetDispositions: buildTargetDispositions(actions, "delete"),
              rationale,
            },
            {
              onSuccess: () => {
                closePanel();
                navigateBackToList();
              },
            }
          );
        }}
      />

      {/* Target row action menu */}
      <TargetActionMenu
        anchorEl={targetMenu.anchorEl}
        open={Boolean(targetMenu.anchorEl)}
        onClose={handleTargetMenuClose}
        onEdit={handleEditTarget}
        onDuplicate={handleDuplicateTarget}
        onMove={handleOpenMoveTarget}
        onArchive={handleArchiveTarget}
        onUnarchive={handleUnarchiveTarget}
        isArchived={Boolean(targets.find((t) => t.id === targetMenu.targetId)?.isArchived)}
        onDelete={handleOpenDeleteTarget}
      />

      <MoveTargetPanel
        open={moveTargetState.open}
        onClose={() => setMoveTargetState({ open: false, targetId: null, targetName: "" })}
        targetName={moveTargetState.targetName}
        availableGoals={moveDestinationGoals}
        onMove={handleConfirmMoveTarget}
        isLoading={isMovingTarget}
      />

      <DeleteTargetDialog
        open={deleteTargetState.open}
        onClose={() => setDeleteTargetState({ open: false, targetId: null, targetName: "" })}
        targetName={deleteTargetState.targetName}
        onConfirm={handleConfirmDeleteTarget}
        isLoading={isDeletingTarget}
      />
    </Stack>
  );
};

export default GoalDetail;
